"use client"
import React, { useState } from 'react';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import { Clock, ShoppingCart } from 'lucide-react';
import { Icons } from '@/components';
import { cn } from '@/lib/utils';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';

// Product type definition
type Product = {
    id: number;
    title: string;
    name: string;
    originalPrice: string;
    price: string;
    image: string;
    discount: number;
}

// Updated product data with Steam game names
const products: Product[] = [
    {
        id: 1,
        title: 'Z1P',
        name: 'PLAYERUNKNOWN\'S BATTLEGROUNDS',
        originalPrice: '۱۳,۵۰۰,۰۰۰',
        price: '۱۲,۳۲۰,۰۰۰',
        image: '/gallery/2.jpg',
        discount: 9
    },
    {
        id: 2,
        title: 'X2P',
        name: 'Call of Duty®: Modern Warfare® II',
        originalPrice: '۱۵,۸۰۰,۰۰۰',
        price: '۱۴,۲۲۰,۰۰۰',
        image: '/gallery/1.jpg',
        discount: 10
    },
    {
        id: 3,
        title: 'Y3P',
        name: 'Grand Theft Auto V',
        originalPrice: '۱۸,۲۰۰,۰۰۰',
        price: '۱۶,۳۸۰,۰۰۰',
        image: '/gallery/3.jpg',
        discount: 11
    },
    {
        id: 4,
        title: 'Y3P',
        name: 'The Witcher 3: Wild Hunt',
        originalPrice: '۱۸,۲۰۰,۰۰۰',
        price: '۱۶,۳۸۰,۰۰۰',
        image: '/gallery/4.jpg',
        discount: 11
    },
    {
        id: 5,
        title: 'Y3P',
        name: 'Red Dead Redemption 2',
        originalPrice: '۱۸,۲۰۰,۰۰۰',
        price: '۱۶,۳۸۰,۰۰۰',
        image: '/gallery/5.jpg',
        discount: 11
    },
    {
        id: 6,
        title: 'Y3P',
        name: 'Cyberpunk 2077',
        originalPrice: '۱۸,۲۰۰,۰۰۰',
        price: '۱۶,۳۸۰,۰۰۰',
        image: '/gallery/6.png',
        discount: 11
    }
];


export default function CardPag() {
    const [activeIndex, setActiveIndex] = useState(0);

    return (
        <div className="relative w-full md:max-w-sm mx-auto p-4 overflow-hidden">
            {/* Background Container */}
            <div className="absolute inset-0 w-full h-full bg-[#19161D] border-[1px] border-[#2A2633] z-10 rounded-[32px] overflow-hidden" />

            {/* Side Decoration */}
            <div className="absolute inset-0 w-full h-full">
                <div className="h-full flex justify-start items-center">
                    <svg
                        className="transform -rotate-90 md:translate-x-[94px] translate-x-[80px] md:w-48 w-40 z-10"
                        xmlns="http://www.w3.org/2000/svg"
                        width="450"
                        height="100"
                        viewBox="0 0 450 100"
                    >
                        <path
                            d="M0,55 C50,55 50,20 100,20 H350 C400,20 400,55 450,55"
                            fill="#0A0512"
                            stroke="#2A2633"
                            strokeWidth="3"
                            strokeLinecap="round"
                        />
                    </svg>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex flex-col md:gap-4 gap-0 relative z-10">
                {/* Header */}
                <div className="items-center justify-between flex">
                    <span className="md:text-sm text-md font-medium bg-gradient-to-r from-primary/80 to-primary bg-clip-text text-gray-300">
                        پیشنهادات امروز
                    </span>
                    <span className="bg-destructive/10 text-destructive text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 font-medium backdrop-blur-sm">
                        <Clock className="w-3.5 h-3.5" />
                        <span>هنوز شروع نشده</span>
                    </span>
                </div>

                {/* Vertical Slider */}
                <div className="flex gap-2 relative">
                    <div className="flex-1 h-[190px] min-h-[190px]">
                        <Swiper
                            modules={[Pagination, Autoplay]}
                            direction="vertical"
                            slidesPerView={1}
                            spaceBetween={30}
                            pagination={{
                                el: '.swiper-pagination',
                                clickable: true,
                                renderBullet: (_, className) => {
                                    return `<span class="${className} !w-2 !h-2 !bg-[hsl(var(--primary))] !opacity-50 hover:!opacity-75 transition-opacity"></span>`;
                                },
                            }}
                            autoplay={{ delay: 3000 }}
                            loop={true}
                            onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                            className="w-full"
                        >
                            {products.map((product) => (
                                <SwiperSlide key={product.id}>
                                    <div className="w-full flex md:justify-center justify-start items-center">
                                        <div className="relative w-[200px] h-[200px] group">
                                            {/* Image Container */}
                                            <div className="absolute inset-0 flex items-center justify-center p-6">
                                                <div className={cn(
                                                    "relative w-full h-full rounded-2xl overflow-hidden",
                                                    "before:absolute before:inset-0 before:bg-gradient-to-t before:from-black/10 before:to-transparent before:z-10"
                                                )}>
                                                    <Image
                                                        src={product.image}
                                                        alt={product.title}
                                                        fill
                                                        className="object-contain transform group-hover:scale-110 transition-transform duration-500"
                                                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                                    />
                                                </div>
                                            </div>

                                            {/* Discount Badge */}
                                            <div className={cn(
                                                "absolute top-2 right-2 text-xs font-bold px-2.5 py-1.5 rounded-full",
                                                "bg-destructive/10 text-destructive shadow-lg backdrop-blur-sm",
                                                "transform group-hover:scale-105 transition-transform z-20",
                                                "border border-destructive/20"
                                            )}>
                                                {product.discount}% تخفیف
                                            </div>
                                        </div>


                                        <div className='h-full flex flex-col gap-5 justify-start md:hidden'>
                                            <div className="text-muted-foreground/70 text-lg font-bold">
                                                {product.name}
                                            </div>

                                            <div className='flex items-center gap-5 w-full pl-3'>
                                                <button className={cn(
                                                    "flex-shrink-0 p-3 rounded-md transition-all duration-300",
                                                    "bg-primary/10 hover:bg-primary/20 text-primary-foreground",
                                                    "hover:shadow-lg hover:shadow-primary/10 active:scale-95",
                                                    "backdrop-blur-sm border border-primary/20 hover:border-primary/30",
                                                    "relative overflow-hidden",
                                                    "before:absolute before:inset-0 before:bg-gradient-to-r before:from-primary/5 before:to-transparent",
                                                    "before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300"
                                                )}>
                                                    <ShoppingCart className="!w-4 !h-4 relative text-[#D325D3] z-10" />
                                                </button>
                                                <div className="text-[#847599] font-bold text-sm">
                                                    {products[activeIndex].price} تومان
                                                </div>



                                            </div>

                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>

                    {/* Pagination Dots */}
                    <div className="flex z-40 flex-col items-center justify-center space-y-2 pr-2 absolute inset-y-0 right-0  transform translate-x-6  md:translate-y-5 -translate-y-4">
                        {products.map((_, index) => (
                            <span
                                key={index}
                                className={cn(
                                    "md:w-2 w-1 rounded-full transition-all duration-300",
                                    index === activeIndex
                                        ? "bg-[hsl(var(--primary))] h-5"
                                        : "bg-[hsl(var(--primary)/30%)] h-2"
                                )}
                            />
                        ))}
                    </div>
                </div>

                {/* Footer - Price & Action */}
                <div className="items-center justify-between hidden md:flex">
                    <div className="text-right flex-1 min-w-0">
                        <div className="line-through text-muted-foreground/70 text-xs">
                            {products[activeIndex].originalPrice}
                        </div>
                        <div className="text-[#847599] font-bold text-lg">
                            {products[activeIndex].price} تومان
                        </div>
                    </div>
                    <button className={cn(
                        "flex-shrink-0 p-3.5 rounded-full transition-all duration-300",
                        "bg-primary/10 hover:bg-primary/20 text-primary-foreground",
                        "hover:shadow-lg hover:shadow-primary/10 active:scale-95",
                        "backdrop-blur-sm border border-primary/20 hover:border-primary/30",
                        "relative overflow-hidden",
                        "before:absolute before:inset-0 before:bg-gradient-to-r before:from-primary/5 before:to-transparent",
                        "before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300"
                    )}>
                        <Icons.cart className="!w-6 !h-6 relative z-10" />
                    </button>
                </div>
            </div>
        </div>
    );
}
